"use client";
import React, { useContext, useMemo, useState, useCallback } from "react";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import useRouteChange from "@stadium/hooks/lib/use-route-change";
import { BYSTADIUM_HOSTNAME } from "@stadium/helpers/src/app-routes";

import { matchesUrl } from "@stadium/helpers/dom";

export type PreviousRouteContextType = {
  history: URL[];
  // eslint-disable-next-line no-unused-vars
  getPreviousUrl: (options?: {
    /**
     * Takes precendence over allowedRoutes
     */
    skippedRoutes?: string[];
    allowSamePathNameAsCurrent?: boolean;
    allowedRoutes?: string[];
  }) => URL;
};

export const PreviousRouteContext = React.createContext<PreviousRouteContextType>({
  history: [],
  getPreviousUrl: () => null,
});

export const usePreviousRouteContext = () => useContext(PreviousRouteContext);

export const PreviousRouteProvider = ({ children }) => {
  const { asPath } = useRouter();

  const [history, setHistory] = useState<Array<URL>>([]);

  const historyChangeHandler = useCallback((asPath: string) => {
    if (asPath === "null") return;
    setHistory((prevHistory) => [...prevHistory, new URL(asPath, window.origin)]);
  }, []);

  useRouteChange(historyChangeHandler);

  const getPreviousUrl: PreviousRouteContextType["getPreviousUrl"] = useCallback(
    ({ skippedRoutes = [], allowSamePathNameAsCurrent = false, allowedRoutes = [] } = {}) => {
      const currentRoute = new URL(
        typeof window !== "undefined" ? window.location.href : asPath,
        BYSTADIUM_HOSTNAME,
      );

      return [...history].reverse().find((prevRoute) => {
        const matchesPathname = (pathname: string) =>
          matchesUrl({ url: prevRoute.pathname, pathname });

        const isSkipped = skippedRoutes.some(matchesPathname);
        if (isSkipped) {
          return;
        }
        const isValidRoute = () => {
          if (allowSamePathNameAsCurrent) {
            return prevRoute.href !== currentRoute.href;
          }
          return prevRoute.pathname !== currentRoute.pathname;
        };

        if (allowedRoutes?.length) {
          if (allowedRoutes.some(matchesPathname)) {
            return isValidRoute();
          }
          return false;
        }
        return isValidRoute();
      });
    },
    [history],
  );

  const providerValue = useMemo(() => ({ history, getPreviousUrl }), [history, getPreviousUrl]);

  return (
    <PreviousRouteContext.Provider value={providerValue}>{children}</PreviousRouteContext.Provider>
  );
};
